import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { LOGIN, REGISTER } from "@/constants/testIds";

const HOME_BY_ROLE = { member: "/cari", photographer: "/fotografer", admin: "/admin" };

const Shell = ({ title, sub, children }) => (
  <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-16">
    <div className="w-full max-w-md rounded-2xl bg-white border border-zinc-200 p-8 sm:p-10 fade-up">
      <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900">{title}</h1>
      <p className="text-sm text-zinc-500 mt-2 mb-8">{sub}</p>
      {children}
    </div>
  </main>
);

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-zinc-700">{label}</Label>
    <Input className="h-11 rounded-xl" required {...props} />
  </div>
);

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(`Selamat datang, ${u.name}`);
      nav(HOME_BY_ROLE[u.role]);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Masuk" sub="Masuk untuk mencari dan membeli fotomu.">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={LOGIN.emailInput} />
        <Field label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid={LOGIN.passwordInput} />
        <Button disabled={loading} className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700" data-testid={LOGIN.submitButton}>{loading ? "Memproses..." : "Masuk"}</Button>
      </form>
      <p className="text-sm text-zinc-500 mt-6 text-center">Belum punya akun? <Link to="/register" className="text-blue-600 font-semibold" data-testid={LOGIN.registerLink}>Daftar</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: params.get("role") === "photographer" ? "photographer" : "member" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Akun berhasil dibuat");
      nav(HOME_BY_ROLE[u.role]);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Buat akun" sub="Pilih peranmu: cari foto sebagai member, atau jual foto sebagai fotografer.">
      <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-zinc-100 mb-6">
        {[["member", "Member"], ["photographer", "Fotografer"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setForm({ ...form, role: v })} data-testid={`register-role-${v}`}
            className={`h-10 rounded-lg text-sm font-semibold transition-colors ${form.role === v ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-5">
        <Field label={form.role === "photographer" ? "Nama studio / fotografer" : "Nama lengkap"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={REGISTER.nameInput} />
        <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={REGISTER.emailInput} />
        <Field label="Password (min. 6 karakter)" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid={REGISTER.passwordInput} />
        <Button disabled={loading} className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700" data-testid={REGISTER.submitButton}>{loading ? "Memproses..." : "Daftar"}</Button>
      </form>
      <p className="text-sm text-zinc-500 mt-6 text-center">Sudah punya akun? <Link to="/login" className="text-blue-600 font-semibold" data-testid={REGISTER.loginLink}>Masuk</Link></p>
    </Shell>
  );
}
